import { useState } from "react";
import { useAuthContext } from "providers/Auth";
import { getErrorMessage } from "utils/error";

import { Layout } from "providers/Layout";
import bankAccountsApi from "api/landlord/bankAccounts";
import partnerApi from "api/landlord/renters";
import { BankAccount } from "types/billing";

import { BankAccountRequiredCard } from "./components/BankAccountRequiredCard";
import { TransactionSection } from "./components/TransactionSection";
import { BillingTable } from "./components/BillingTable";
import { ChangeDayModal } from "./components/ChangeDayModal";
import { ChangeBankAccountModal } from "./components/ChangeBankAccountModal";

import { useBillingController } from "./controller";
import { getCSVLineItems } from "../statements-id/utils";
import { toast } from "react-toastify";
import { InputSize, Input } from "shared/components";
import { Loader } from "shared/components/Loader";
import { Header } from "./components/Header";
import { useQueryClient } from "@tanstack/react-query";
import { StatementsIdView } from "../statements-id";
import moment from "moment";

export const StatementsView = () => {
  const {
    statements,
    setStatementPage,
    statementUpcoming,
    statementUpcomingIsLoading,
    searchQuery,
    payoutAccount,
    invoiceAccount,
    setSearchQuery,
    statementsIsLoading
  } = useBillingController();
  const queryClient = useQueryClient();
  const {
    partner: currentPartner,
    dashboard,
    refreshPartnerData
  } = useAuthContext();

  const [bankAccountModalOpen, setBankAccountModalOpen] = useState(false);

  const [dayModalOpen, setDayModalOpen] = useState(false);

  const updateBillingDay = (billingDay: number) =>
    partnerApi
      .updateBillingDay(billingDay)
      .then(() => {
        toast.success("Billing day has been successfully updated");
        return queryClient.invalidateQueries({
          queryKey: ["STATEMENT_UPCOMING"]
        });
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        toast.error(errorMessage);
      });

  const handleCreateBankAccount = async (bankAccount: BankAccount) => {
    try {
      await bankAccountsApi.postBankAccounts({
        bank_account: bankAccount,
        usage_kinds: ["payout", "invoice"]
      });
      toast.success("Bank account has been successfully updated");
      setBankAccountModalOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ["BANK_ACCOUNTS"]
      });
      await refreshPartnerData();
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
    }
  };

  const bankAccountRequired = dashboard?.billing.bank_account_required;

  const [selectedStatementId, setSelectedStatementId] = useState<number | null>(
    null
  );

  if (statementUpcomingIsLoading || statementsIsLoading)
    return (
      <Layout
        header={<Header />}
        footer={
          bankAccountRequired && (
            <BankAccountRequiredCard plaidLink={dashboard.billing.plaid_link} />
          )
        }
        sectionClassName={bankAccountRequired ? "hidden" : ""}
      >
        <div className="flex items-center justify-center h-full">
          <Loader />
        </div>
      </Layout>
    );

  return (
    <>
      <div className={selectedStatementId ? "no-print" : ""}>
        <Layout header={<Header />} sectionClassName="relative">
          <div className="animate-appear">
            {bankAccountRequired ? (
              <BankAccountRequiredCard
                plaidLink={dashboard.billing.plaid_link}
                onManualConnect={() => {
                  setBankAccountModalOpen(true);
                }}
              />
            ) : (
              <div>
                <div className="mb-4 flex flex-wrap gap-2 md:flex-nowrap">
                  <TransactionSection
                    accountType={
                      statementUpcoming.balance > 0
                        ? payoutAccount?.name
                        : invoiceAccount?.name
                    }
                    maskedNumber={
                      statementUpcoming.balance > 0
                        ? payoutAccount?.last4 || ""
                        : invoiceAccount?.last4 || ""
                    }
                    amount={statementUpcoming.balance || 0}
                    billingDate={moment(statementUpcoming.billing_date).format(
                      "D MMM YYYY"
                    )}
                    onChangeDay={() => setDayModalOpen(true)}
                    onChangeBankAccount={() => setBankAccountModalOpen(true)}
                  />
                </div>
                <div className="max-w-[271px] py-2">
                  <Input
                    id="search"
                    autoComplete="off"
                    placeholder="Search"
                    size={InputSize.SM}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setStatementPage(1);
                    }}
                  />
                </div>
                <BillingTable
                  data={statements?.items || []}
                  isLoading={statementsIsLoading}
                  pageCount={Number(statements?.page_count)}
                  currentPage={Number(statements?.current_page)}
                  getCSVLineItems={getCSVLineItems}
                  setPage={setStatementPage}
                  setSelectedStatementId={setSelectedStatementId}
                />
              </div>
            )}
          </div>
          <ChangeDayModal
            isOpen={dayModalOpen}
            day={currentPartner?.invoice_billing_day || 1}
            onDismiss={() => setDayModalOpen(false)}
            onSubmit={(day) => {
              updateBillingDay(day);
            }}
          />
          {bankAccountModalOpen && (
            <ChangeBankAccountModal
              bankAccountRequired={bankAccountRequired}
              isOpen={bankAccountModalOpen}
              onDismiss={() => setBankAccountModalOpen(false)}
              onSubmit={handleCreateBankAccount}
            />
          )}
        </Layout>
      </div>
      {selectedStatementId ? (
        <StatementsIdView
          statementId={Number(selectedStatementId)}
          printRequested={true}
          onPrint={() => {
            setSelectedStatementId(null);
          }}
        />
      ) : null}
    </>
  );
};
